// TODO: remove the "_" import
import _, { chain, flatten, isArray, isEmpty, isObject, isString, map, uniq } from 'lodash';
import { StringUtils } from './StringUtils';
import {
    EntryPoint,
    LocationUserSubscription,
    LocationProductType,
    FieldType,
    LocationType,
    GeoType,
    LegType,
} from '../models';
import { Config } from './Config';
import { ABUtils } from './ABUtils';

export const RESERVED_ACCOMMODATION_SLOTS = 2;

export const LocationUtils = {
    RESERVED_ACCOMMODATION_SLOTS,

    filterSuggestedLocations({
        fetchedLocations,
        displayedWord,
        maxAutocompleteSuggestions = 1,
        locationProductType = LocationProductType.FLIGHT,
        filterByDisplayedWord = true,
    }) {
        if (!fetchedLocations) {
            return [];
        }
        _.mixin({
            even: (list) => {
                let parsedList = list;
                if (isObject(list)) {
                    parsedList = flatten(map(isArray(list) ? flatten(list) : list, _.even));
                }
                return parsedList;
            },
        });
        const omitted = ['type', 'geoNodeId', 'relatedLocations'];
        const flightLocations =
            locationProductType === LocationProductType.HOTEL
                ? fetchedLocations.filter((location) => location.type !== LocationType.HOTEL)
                : fetchedLocations;

        let filteredLocations = filterByDisplayedWord
            ? flightLocations.filter((location) => {
                return chain(location)
                    .omit(omitted)
                    .values()
                    .even()
                    .some((value) => {
                        if (!isString(value)) {
                            return false;
                        }
                        return this.locationContains(value.toLowerCase(), displayedWord.toLowerCase());
                    }, this)
                    .value();
            })
            : flightLocations;

        if (locationProductType === LocationProductType.HOTEL) {
            const hotelLocations =
                locationProductType === LocationProductType.HOTEL
                    ? fetchedLocations.filter((location) => location.type === LocationType.HOTEL)
                    : [];
            if (filteredLocations.length >= maxAutocompleteSuggestions) {
                filteredLocations = filteredLocations.slice(0, maxAutocompleteSuggestions - 1);
            }
            const hotelSuggestionsDisplayed = maxAutocompleteSuggestions - filteredLocations.length;
            filteredLocations = filteredLocations.concat(
                hotelLocations.slice(0, hotelSuggestionsDisplayed),
            );
        } else if (locationProductType === LocationProductType.FLIGHT || locationProductType === LocationProductType.TRAIN) {
            filteredLocations = uniq(filteredLocations, (location) => {
                return map(location.relatedLocations, 'geoNodeId')
                    .sort()
                    .toString();
            });
        }
        return filteredLocations.slice(0, maxAutocompleteSuggestions);
    },

    filterSuggestedLocationsSearchByRegion({
        fetchedLocations,
        maxAutocompleteSuggestions = 1,
        maxAccommodationAutocompletesSuggestions = 1,
        reservedAccommodationSlots = RESERVED_ACCOMMODATION_SLOTS,
    }) {
        if (!fetchedLocations) {
            return [];
        }
        _.mixin({
            even: (list) => {
                let parsedList = list;
                if (isObject(list)) {
                    parsedList = flatten(map(isArray(list) ? flatten(list) : list, _.even));
                }
                return parsedList;
            },
        });

        const filteredLocations = fetchedLocations.filter((location) => {
            return chain(location)
                .values()
                .even()
                .some((value) => {
                    return !(!isString(value));
                }, this)
                .value();
        });

        let [hotelLocations, restLocations] = filteredLocations.reduce(
            ([hotels, rest], suggestion) =>
                suggestion.type === GeoType.ACCOMMODATION
                    ? [[...hotels, suggestion], rest]
                    : [hotels, [...rest, suggestion]],
            [[], []],
        );

        return this.allocateSuggestionsByRegionAndAccommodation({
            restLocations,
            hotelLocations,
            maxAutocompleteSuggestions,
            maxAccommodationAutocompletesSuggestions,
            reservedAccommodationSlots,
        });
    },

    calculateRegionAndAccommodationSlots({
        totalRegions = 0,
        totalAccommodations = 0,
        maxAutocompleteSuggestions = 1,
        maxAccommodationAutocompletesSuggestions = maxAutocompleteSuggestions,
        reservedAccommodationSlots = RESERVED_ACCOMMODATION_SLOTS,
    }) {
        if (maxAutocompleteSuggestions <= 0) {
            return { regionSlots: 0, accommodationSlots: 0 };
        }

        const effectiveReservedSlots = Math.min(
            reservedAccommodationSlots,
            maxAccommodationAutocompletesSuggestions,
        );
        const reservedSlots = Math.min(totalAccommodations, effectiveReservedSlots);
        const maxRegionSlots = Math.max(0, maxAutocompleteSuggestions - reservedSlots);
        const regionSlots = Math.min(totalRegions, maxRegionSlots);

        const remainingSlots = Math.max(0, maxAutocompleteSuggestions - regionSlots);
        const accommodationSlots = Math.min(
            totalAccommodations,
            remainingSlots,
            maxAccommodationAutocompletesSuggestions,
        );

        return { regionSlots, accommodationSlots };
    },

    allocateSuggestionsByRegionAndAccommodation({
        restLocations = [],
        hotelLocations = [],
        maxAutocompleteSuggestions = 1,
        maxAccommodationAutocompletesSuggestions = maxAutocompleteSuggestions,
        reservedAccommodationSlots = RESERVED_ACCOMMODATION_SLOTS,
    }) {
        const { regionSlots, accommodationSlots } = this.calculateRegionAndAccommodationSlots({
            totalRegions: restLocations ? restLocations.length : 0,
            totalAccommodations: hotelLocations ? hotelLocations.length : 0,
            maxAutocompleteSuggestions,
            maxAccommodationAutocompletesSuggestions,
            reservedAccommodationSlots,
        });

        const regionSuggestions = restLocations ? restLocations.slice(0, regionSlots) : [];
        const hotelSuggestions = hotelLocations ? hotelLocations.slice(0, accommodationSlots) : [];

        return regionSuggestions.concat(hotelSuggestions);
    },

    ignoreSpaces(token, minCharsWithoutSpaces) {
        let tokenMinChars = 0;
        let tokenWithSpaces = '';
        for (let i = 0; i < token.length && tokenMinChars < minCharsWithoutSpaces; i++) {
            const c = token[i];
            tokenWithSpaces += c;
            if (c !== ' ') {
                tokenMinChars++;
            }
        }
        return tokenWithSpaces;
    },

    getCleanedKey(displayedWord) {
        return StringUtils.replaceNonAlphaNumeric(displayedWord.toLowerCase(), ' ', true);
    },

    getLocationsKey(displayedWord, autocompleteOptions = Config.get('autocompleteOptions')) {
        const tokenCleaned = this.getCleanedKey(displayedWord);
        const isCoherent = this.isCharCoherentWithLanguage(tokenCleaned, autocompleteOptions);
        const minCharsWithoutSpaces = isCoherent
            ? autocompleteOptions.autocompleteMinChars
            : autocompleteOptions.defaultMinChars;
        return this.ignoreSpaces(tokenCleaned.trim(), minCharsWithoutSpaces);
    },

    getFullLocationsKey(displayedWord) {
        return this.getCleanedKey(displayedWord).trim();
    },

    isCharCoherentWithLanguage(word, autocompleteOptions) {
        const coherentCharsRegex = new RegExp(autocompleteOptions.coherentCharsRegex, 'g');
        const matchingChars = word.match(coherentCharsRegex);
        return matchingChars !== null && matchingChars.length === word.length;
    },

    isKeywordLongEnoughAccordingWithLanguage(displayedWord, autocompleteOptions, activeProduct, departureOrArrival) {
        const minChars = this.getMinCharsForAutocomplete(displayedWord, autocompleteOptions, activeProduct, departureOrArrival);
        const token = StringUtils.replaceNonAlphaNumeric(displayedWord.trim(), '', false);
        return token.length >= minChars;
    },

    // Hotels, and the DP arrival field in the SE-3806 variant B, query the accommodation
    // autocomplete: they search from the first letter and key their results by
    // the whole word, so every letter typed is a new query.
    usesAccommodationAutocomplete(activeProduct, departureOrArrival = LegType.ARRIVAL) {
        return activeProduct === EntryPoint.HOTELS
            || (activeProduct === EntryPoint.DP
                && departureOrArrival === LegType.ARRIVAL
                && !!ABUtils.getABs().isDpGeoRegionAutocomplete);
    },

    getMinCharsForAutocomplete(displayedWord, autocompleteOptions, activeProduct, departureOrArrival) {
        if (this.usesAccommodationAutocomplete(activeProduct, departureOrArrival)) {
            return autocompleteOptions.accommodationAutocompleteMinChars;
        }
        return this.isCharCoherentWithLanguage(displayedWord, autocompleteOptions)
            ? autocompleteOptions.autocompleteMinChars
            : autocompleteOptions.defaultMinChars;
    },

    getAutocompleteMaxSuggestions(activeProduct, autocompleteOptions) {
        const { isMoreOptionsInAutocomplete } = ABUtils.getABs();
        if (activeProduct === EntryPoint.HOTELS && isMoreOptionsInAutocomplete) {
            return autocompleteOptions.accommodationAutocompleteMaxSuggestions;
        }
        return autocompleteOptions.autocompleteMaxSuggestions;
    },

    getAutocompleteMaxAccommodationsSuggestions(activeProduct, autocompleteOptions) {
        const DEFAULT_MAX_ACCOMMODATIONS_SUGGESTIONS = 1;
        const { isMoreOptionsInAutocomplete } = ABUtils.getABs();
        if (activeProduct === EntryPoint.HOTELS && isMoreOptionsInAutocomplete) {
            return autocompleteOptions.accommodationAutocompleteMaxAccommodations;
        }
        return DEFAULT_MAX_ACCOMMODATIONS_SUGGESTIONS;
    },

    getLocationProductType(departureOrArrival, activeProduct) {
        return activeProduct === EntryPoint.HOTELS
            ? LocationProductType.HOTEL
            : departureOrArrival === 'ARRIVAL' && activeProduct === EntryPoint.DP
                ? LocationProductType.DP
                : activeProduct === EntryPoint.TRAINS ? LocationProductType.TRAIN : LocationProductType.FLIGHT;
    },

    getFetchedLocations(
        fetchedLocations,
        locationProductType,
        departureOrArrival,
        activeProduct,
        displayedWord,
        subscription,
        autocompleteOptions = Config.get('autocompleteOptions'),
        useFullSearch = false,
    ) {
        const isHotelEntryPoint = activeProduct === EntryPoint.HOTELS;
        // The key follows the autocomplete being queried, the bucket the product:
        // DP arrival in variant B keys by whole word but still reads from its own slot.
        const key = this.usesAccommodationAutocomplete(activeProduct, departureOrArrival)
            ? this.getCleanedKey(displayedWord).trim()
            : useFullSearch
                ? this.getFullLocationsKey(displayedWord)
                : this.getLocationsKey(displayedWord, autocompleteOptions);
        const locations = isHotelEntryPoint
            ? fetchedLocations[LocationProductType.HOTEL][subscription]
            : fetchedLocations[locationProductType][departureOrArrival];
        return locations.locations[key];
    },

    getFirstSuggestedLocation({
        fetchedLocations,
        displayedWord,
        departureOrArrival,
        activeProduct,
    }) {
        let suggestion = null;
        const useFullSearch = Config.get('ab')?.isNewItineraryAutocomplete || false;
        const autocompleteOptions = Config.get('autocompleteOptions');
        const locationProductType = (activeProduct === EntryPoint.HOTELS) ? null : this.getLocationProductType(departureOrArrival, activeProduct);
        const subscription = this.getLocationSubscription();
        const fetchedLocation = this.getFetchedLocations(
            fetchedLocations,
            locationProductType,
            departureOrArrival,
            activeProduct,
            displayedWord,
            subscription,
            autocompleteOptions,
            useFullSearch,
        );
        const locations = fetchedLocation?.locationAutocomplete;
        const filterParams = {
            fetchedLocations: locations,
            displayedWord,
            maxAutocompleteSuggestions: this.getAutocompleteMaxSuggestions(
                activeProduct,
                autocompleteOptions,
            ),
            maxAccommodationAutocompletesSuggestions: this.getAutocompleteMaxAccommodationsSuggestions(
                activeProduct,
                autocompleteOptions,
            ),
            locationProductType,
            filterByDisplayedWord: !useFullSearch,
        };
        const suggestionValue = activeProduct === EntryPoint.HOTELS ?
            LocationUtils.filterSuggestedLocationsSearchByRegion(filterParams) : LocationUtils.filterSuggestedLocations(filterParams);
        if (!isEmpty(suggestionValue)) {
            const firstSuggestion = suggestionValue[0];
            if (
                (activeProduct === EntryPoint.FLIGHTS ||
                    (activeProduct === EntryPoint.DP &&
                        departureOrArrival === FieldType.DEPARTURE)) &&
                !firstSuggestion.iata &&
                firstSuggestion.relatedLocations?.length > 0
            ) {
                suggestion = firstSuggestion.relatedLocations[0];
            } else {
                suggestion = firstSuggestion;
            }
        }
        return suggestion;
    },

    locationContains(location, searchWord) {
        const replacedSearchWord = StringUtils.replaceNonAlphaNumeric(searchWord, '', true);
        return (
            StringUtils.replaceNonAlphaNumeric(location, '', true).indexOf(replacedSearchWord) >=
                0 && replacedSearchWord.length
        );
    },

    getFieldText(location, direction, activeProduct = null) {
        if (!location) return '';
        const { country, city, iata, caption } = location;
        if (activeProduct === EntryPoint.HOTELS) {
            return this.getInputText(location, activeProduct);
        }
        if (direction === 'rtl') {
            return `${country} ,${city}${iata ? ` - ${iata}` : ''}`;
        }
        // A geoRegion location (SE-3806) has no country, so fall back to its name
        // instead of leaving the field blank.
        return city && country
            ? `${iata ? `${iata} - ` : ''}${city}, ${country}`
            : caption || this.getInputText(location) || '';
    },

    getFieldTextForHotel(location, direction) {
        if (!location) return '';
        const { iata, name, description } = location;
        if (direction === 'rtl') {
            return `${name} ,${description}${iata ? ` - ${iata}` : ''}`;
        }
        return `${iata ? `${iata} - ` : ''} ${name}${description ? `, ${description}` : ''}`;
    },

    getInputText(location, activeProduct = null) {
        if (!location) return '';
        const { displayName, city, country, name, region } = location;
        const locationName = displayName || city || name;
        if (activeProduct === EntryPoint.HOTELS) {
            return `${locationName}${region ? `, ${region}` : ''}${city ? `, ${city}` : ''}${country ? `, ${country}` : ''}`;
        }
        return locationName;
    },

    getLocationSubscription() {
        return Config.get('isPrimeCookieActive')
            ? LocationUserSubscription.PRIME
            : LocationUserSubscription.NON_PRIME;
    },
};
